import React, { useMemo, useRef, useState } from "react";
import {
  bookendFonts,
  brandFontStack,
  brandReadability,
  defaultBrandKit,
  maxLogoBytes,
  minLogoPixels,
  type BrandKit as Kit,
} from "@veyso/core";
import { api } from "../api.js";
import { NarratorPicker } from "./NarratorPicker.js";
import { ColorField, Field, Select, Toggle } from "../components/ui.js";

/**
 * The workspace mark, and what happens when there is not one.
 *
 * Reading a logo off a website works often and not always: plenty of sites are
 * a JavaScript shell with no markup to read, serve an icon far too small to
 * put in a 1920-pixel frame, or offer it in a format we will not embed. When
 * that happens the launch video has no mark on it at all — which is a poor
 * thing to discover from the finished video, so it is said here instead, while
 * somebody is looking at the page that can fix it.
 */
const LogoSection: React.FC<{ kit: Kit; onChange: (kit: Kit) => void }> = ({ kit, onChange }) => {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choose = async (file: File) => {
    setError(null);
    setBusy(true);
    try {
      const dataUri = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("That file could not be read"));
        reader.readAsDataURL(file);
      });
      const stored = await api.uploadBrandLogo(dataUri);
      onChange({ ...kit, logoKey: stored.logoKey });
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
      // So choosing the same file twice in a row still fires a change.
      if (input.current) input.current.value = "";
    }
  };

  return (
    <section className="panel flex flex-col gap-4 p-5">
      <div>
        <h2 className="text-[13px] font-semibold text-ink-100">Logo</h2>
        <p className="mt-1 text-[12px] leading-[1.5] text-ink-300">
          Used in the launch video and on title cards. With no logo image the product name is set as type instead, in
          your title typeface.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-[var(--edge)] bg-bay-800">
          {kit.logoKey ? (
            <img src={`/assets/${kit.logoKey}`} alt="" className="h-full w-full object-contain p-2" />
          ) : (
            <span className="text-[10.5px] text-ink-400">none</span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            {/*
              `.btn` alone is only the layout — height, padding, radius, and a
              transparent border. Every visible button here carries a variant,
              and without one these two rendered as bare text.

              Primary while there is no logo, because that is the action the
              warning below is pointing at; secondary once there is one, when
              replacing is a thing you might do rather than the thing to do.
            */}
            <button
              type="button"
              className={`btn ${kit.logoKey ? "btn-secondary" : "btn-primary"}`}
              disabled={busy}
              onClick={() => input.current?.click()}
            >
              {busy ? "Uploading…" : kit.logoKey ? "Replace" : "Upload a logo"}
            </button>
            {kit.logoKey ? (
              <button type="button" className="btn btn-danger" onClick={() => onChange({ ...kit, logoKey: undefined })}>
                Remove
              </button>
            ) : null}
          </div>
          <p className="text-[11.5px] text-ink-400">
            {`SVG, PNG, WebP or JPEG · at least ${minLogoPixels}px on the short side · under ${Math.round(maxLogoBytes / 1024)}KB`}
          </p>
        </div>

        <input
          ref={input}
          type="file"
          accept="image/svg+xml,image/png,image/webp,image/jpeg"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void choose(file);
          }}
        />
      </div>

      {error ? <p className="text-[12px] text-signal-bad">{error}</p> : null}

      {/* Only once there is a logo. A switch governing something that does not
          exist is a control that appears to do nothing. */}
      {kit.logoKey ? (
        <Toggle
          label="Show the logo on title cards"
          hint="Off leaves the card as type alone. The launch video uses the logo either way."
          checked={kit.showLogo}
          onChange={(value) => onChange({ ...kit, showLogo: value })}
        />
      ) : null}

      {/*
        The warning the whole section exists for. Not an error — nothing is
        broken — but the consequence is invisible until a video comes out
        without a mark on it, and by then the moment to fix it has passed.

        `signal-warn` rather than a Tailwind amber. The first version set
        amber-200 on an amber-500 tint, which measures 1.15:1 — the palette's
        ambers are chosen to sit on a dark ground and this app is light, so the
        text all but disappeared.

        The token is #b45309, which is what the contrast warning two sections
        down already uses. On a 5% tint of itself over white it measures
        4.68:1, clearing AA for body text; at 10% it is 4.39:1 and does not,
        which is why the tint is this faint.
      */}
      {!kit.logoKey ? (
        <p className="rounded-[6px] border border-signal-warn/30 bg-signal-warn/5 p-3 text-[12px] leading-[1.55] text-signal-warn">
          No logo is set, and we cannot pull one from your website automatically — many sites have none we can use.
          Until you upload one, your launch video and title cards will set the product name as type instead of showing
          your mark.
        </p>
      ) : null}
    </section>
  );
};

/**
 * The workspace's default look and voice.
 *
 * A workspace makes the same video over and over — the same product, the same
 * palette, the same voice — and answering that per project is how the fifth one
 * ends up looking like a different company made it. Set here once; a new
 * project starts from it and can still be changed after.
 *
 * The preview is the point of the page. Four hex fields and two dropdowns are
 * not a brand, and nobody can tell whether they have chosen well by reading
 * them — so the card underneath is drawn with the real fonts and the real
 * colours, at the proportions a title card actually uses.
 */
export const BrandKitPanel: React.FC<{ kit: Kit; onChange: (kit: Kit) => void }> = ({ kit, onChange }) => {
  const set = <K extends keyof Kit>(key: K, value: Kit[K]) => onChange({ ...kit, [key]: value });
  const setColor = (key: keyof Kit["colors"], value: string) =>
    onChange({ ...kit, colors: { ...kit.colors, [key]: value } });

  // Checked as you type rather than on save: a colour you cannot read is worth
  // knowing about while the picker is still open.
  const contrast = useMemo(
    () => brandReadability(kit.colors.text, kit.colors.background),
    [kit.colors.text, kit.colors.background],
  );

  const fontOptions = bookendFonts.map((font) => ({ value: font.family, label: font.family }));

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-[22px] font-semibold tracking-tight text-ink-100">Brand kit</h1>
        <p className="mt-1 text-[13px] text-ink-300">
          The default look and voice for every new video here. Each project can still be changed afterwards.
        </p>
      </div>

      <LogoSection kit={kit} onChange={onChange} />

      <section className="panel flex flex-col gap-4 p-5">
        <h2 className="text-[13px] font-semibold text-ink-100">Colours</h2>
        <div className="grid grid-cols-2 gap-3">
          <ColorField label="Text" value={kit.colors.text} onChange={(value) => setColor("text", value)} />
          <ColorField
            label="Background"
            value={kit.colors.background}
            onChange={(value) => setColor("background", value)}
          />
          <ColorField label="Accent" value={kit.colors.accent} onChange={(value) => setColor("accent", value)} />
          <ColorField
            label="Second accent"
            value={kit.colors.accentAlt}
            onChange={(value) => setColor("accentAlt", value)}
          />
        </div>

        {/* Said plainly, with the number, because "improve contrast" is advice
            nobody can act on and "2.1:1" is. */}
        {!contrast.readable ? (
          <p className="text-[11px] leading-relaxed text-signal-warn">
            Text on background is {contrast.ratio}:1. Below about 4.5:1 a subtitle is hard to read and a small caption
            is not readable at all.
          </p>
        ) : null}
      </section>

      <section className="panel flex flex-col gap-4 p-5">
        <h2 className="text-[13px] font-semibold text-ink-100">Type</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Title font">
            <Select value={kit.titleFont} options={fontOptions} onChange={(value) => set("titleFont", value)} />
          </Field>
          <Field label="Body font">
            <Select value={kit.bodyFont} options={fontOptions} onChange={(value) => set("bodyFont", value)} />
          </Field>
        </div>
        <p className="text-[11px] leading-relaxed text-ink-400">
          Both ship with the application, so your finished video uses the real face rather than whatever happens to be
          installed on the machine that made it.
        </p>
      </section>

      <section className="panel flex flex-col gap-3 p-5">
        <h2 className="text-[13px] font-semibold text-ink-100">Tone of voice</h2>
        <textarea
          className="input min-h-[70px] resize-y"
          value={kit.tone}
          maxLength={400}
          placeholder="Direct and unhurried. Explains the why before the how."
          onChange={(event) => set("tone", event.target.value)}
        />
        <p className="text-[11px] leading-relaxed text-ink-400">
          Written into the prompt that drafts the narration, for any project that has not asked for something more
          specific of its own.
        </p>

        {/* The other half of how a company sounds. The tone decides what the
            narration says; this decides who says it, and the two belong on one
            page rather than one here and one in provider settings. */}
        <div className="mt-1 border-t border-[var(--edge)] pt-4">
          <h3 className="mb-1 text-[12.5px] font-semibold text-ink-100">Narrator</h3>
          <p className="mb-3 text-[11px] leading-relaxed text-ink-400">
            Used for launch videos and for any project that has not chosen a voice of its own.
          </p>
          <NarratorPicker value={kit.voiceId} onChange={(voiceId) => set("voiceId", voiceId)} />
        </div>
      </section>

      <section className="panel flex flex-col gap-3 p-5">
        <h2 className="text-[13px] font-semibold text-ink-100">Preview</h2>
        {/* A container, so the type inside can be sized in `cqw` and stay in
            proportion at any panel width — the same relationship a card keeps
            between its font size and the frame. Without this the units have
            nothing to resolve against and the title renders at its cap. */}
        <div
          className="relative overflow-hidden rounded-[8px]"
          style={{ aspectRatio: "16 / 9", background: kit.colors.background, containerType: "inline-size" }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-[3%] px-[10%]">
            {/* Sized in `cqw` like the type beside it, so the mark keeps its
                proportion to the frame at any panel width — a logo fixed in
                pixels looks right in this panel and wrong in a 1920 frame. */}
            {kit.logoKey && kit.showLogo ? (
              <img
                src={`/assets/${kit.logoKey}`}
                alt=""
                className="mb-[2%] object-contain"
                style={{ height: "clamp(22px, 9cqw, 72px)", maxWidth: "40%" }}
              />
            ) : null}
            <span
              className="text-center leading-[1.05]"
              style={{
                fontFamily: brandFontStack(kit.titleFont),
                color: kit.colors.text,
                fontWeight: 800,
                fontSize: "clamp(18px, 5.2cqw, 42px)",
              }}
            >
              Connect Discord to your workspace
            </span>
            <span
              className="text-center"
              style={{
                fontFamily: brandFontStack(kit.bodyFont),
                color: kit.colors.text,
                opacity: 0.78,
                fontSize: "clamp(10px, 2.2cqw, 18px)",
              }}
            >
              A ninety-second walkthrough
            </span>
            <span className="mt-[2%] h-[3px] w-[14%] rounded-full" style={{ background: kit.colors.accent }} />
          </div>
        </div>
        <p className="text-[11px] leading-relaxed text-ink-400">
          A title card at the proportions one is actually drawn at, in the real faces.
        </p>
      </section>

      <button
        className="self-start text-[11px] text-ink-400 underline-offset-2 hover:text-ink-200 hover:underline"
        onClick={() => onChange({ ...defaultBrandKit })}
      >
        Reset to the defaults
      </button>
    </div>
  );
};
